/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: RingGroupAdvancedSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Call Center module.
----------------------------------------------------------------*/

import { useCallback, useMemo } from 'react';
import { Settings } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { FormBox } from '../../components/ui/FormBox';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import Toggle from '../../components/ui/Toggle';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select-shadcn';

type ToggleOption = 'enabled' | 'disabled';
type MissedCallAction = 'none' | 'email' | 'sms' | 'internal';
type ForwardOption = 'disabled' | 'number';

interface FormState {
  distinctiveRing: string;
  ringback: string;
  callScreen: ToggleOption;
  callForward: ToggleOption;
  followMe: ToggleOption;
  missedCallAction: MissedCallAction;
  forwardEnabled: ForwardOption;
  forwardNumber: string;
  forwardTollAllow: string;
  record: boolean;
  callTimeoutOverride: string;
  presenceId: string;
}

interface RingGroupAdvancedSectionProps {
  formData: FormState;
  onFieldChange: <K extends keyof FormState>(field: K, value: FormState[K]) => void;
}

const RINGBACK_PRESET_OPTIONS = [
  { value: '${us-ring}', labelKey: 'usRing' },
  { value: '${uk-ring}', labelKey: 'ukRing' },
  { value: '${fr-ring}', labelKey: 'frRing' },
  { value: 'local_stream://default', labelKey: 'default' },
  { value: 'silence', labelKey: 'silence' },
  { value: 'music/default', labelKey: 'musicDefault' }
];

const RINGBACK_PRESETS = RINGBACK_PRESET_OPTIONS.map((option) => option.value);

export function RingGroupAdvancedSection({
  formData,
  onFieldChange
}: RingGroupAdvancedSectionProps) {
  const { t } = useI18n();

  const ringbackPreset = useMemo(() => {
    const normalized = formData.ringback.trim();
    return RINGBACK_PRESETS.includes(normalized) ? normalized : 'custom';
  }, [formData.ringback]);

  const handleRingbackPresetChange = useCallback(
    (value: string) => {
      if (value === 'custom') {
        onFieldChange('ringback', '' as FormState['ringback']);
        return;
      }
      onFieldChange('ringback', value as FormState['ringback']);
    },
    [onFieldChange]
  );

  return (
    <FormBox title={t('ringGroups.form.sections.advancedSettings')} icon={<Settings className="h-5 w-5" />}>
      <div className="grid grid-cols-12 gap-3">
        {/* Ring Back Tone */}
        <SimpleTooltip content={t('ringGroups.form.fields.ringbackType.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="ringbackPreset" className="text-left">
            {t('ringGroups.form.fields.ringbackType.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Select value={ringbackPreset} onValueChange={handleRingbackPresetChange}>
            <SelectTrigger id="ringbackPreset">
              <SelectValue placeholder={t('ringGroups.form.fields.ringback.placeholder')} />
            </SelectTrigger>
            <SelectContent>
              {RINGBACK_PRESET_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {t(`ringGroups.form.fields.ringback.options.${option.labelKey}`)}
                </SelectItem>
              ))}
              <SelectItem value="custom">
                {t('ringGroups.form.fields.ringback.options.custom')}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Ring Back Tone Custom - Conditional */}
        {ringbackPreset === 'custom' && (
          <>
            <div className="col-span-12 md:col-span-6 lg:col-span-3"></div>
            <div className="col-span-12 md:col-span-6 lg:col-span-9">
              <Input
                id="ringbackCustom"
                value={formData.ringback}
                onChange={(event) => onFieldChange('ringback', event.target.value)}
                placeholder={t('ringGroups.form.fields.ringbackType.placeholder')}
              />
            </div>
          </>
        )}

        <SimpleTooltip content={t('ringGroups.form.fields.distinctiveRing.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="distinctiveRing" className="text-left">
            {t('ringGroups.form.fields.distinctiveRing.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="distinctiveRing"
            value={formData.distinctiveRing}
            onChange={(event) => onFieldChange('distinctiveRing', event.target.value)}
            placeholder={t('ringGroups.form.fields.distinctiveRing.placeholder')}
          />
        </div>

        <SimpleTooltip content={t('ringGroups.form.fields.callScreen.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label className="text-left">
            {t('ringGroups.form.fields.callScreen.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Select value={formData.callScreen} onValueChange={(value) => onFieldChange('callScreen', value as ToggleOption)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="disabled">{t('ringGroups.form.fields.callScreen.options.disabled')}</SelectItem>
              <SelectItem value="enabled">{t('ringGroups.form.fields.callScreen.options.enabled')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <SimpleTooltip content={t('ringGroups.form.fields.callForward.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label className="text-left">
            {t('ringGroups.form.fields.callForward.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Select value={formData.callForward} onValueChange={(value) => onFieldChange('callForward', value as ToggleOption)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="disabled">{t('ringGroups.form.fields.callForward.options.disabled')}</SelectItem>
              <SelectItem value="enabled">{t('ringGroups.form.fields.callForward.options.enabled')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <SimpleTooltip content={t('ringGroups.form.fields.followMe.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label className="text-left">
            {t('ringGroups.form.fields.followMe.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Select value={formData.followMe} onValueChange={(value) => onFieldChange('followMe', value as ToggleOption)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="disabled">{t('ringGroups.form.fields.followMe.options.disabled')}</SelectItem>
              <SelectItem value="enabled">{t('ringGroups.form.fields.followMe.options.enabled')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <SimpleTooltip content={t('ringGroups.form.fields.missedCallAction.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label className="text-left">
            {t('ringGroups.form.fields.missedCallAction.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Select
            value={formData.missedCallAction}
            onValueChange={(value) => onFieldChange('missedCallAction', value as MissedCallAction)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t('ringGroups.form.fields.missedCallAction.options.none')}</SelectItem>
              <SelectItem value="email">{t('ringGroups.form.fields.missedCallAction.options.email')}</SelectItem>
              <SelectItem value="sms">{t('ringGroups.form.fields.missedCallAction.options.sms')}</SelectItem>
              <SelectItem value="internal">{t('ringGroups.form.fields.missedCallAction.options.internal')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <SimpleTooltip content={t('ringGroups.form.fields.forwardEnabled.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label className="text-left">
            {t('ringGroups.form.fields.forwardEnabled.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Select
            value={formData.forwardEnabled}
            onValueChange={(value) => onFieldChange('forwardEnabled', value as ForwardOption)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="disabled">{t('ringGroups.form.fields.forwardEnabled.options.disabled')}</SelectItem>
              <SelectItem value="number">{t('ringGroups.form.fields.forwardEnabled.options.number')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {formData.forwardEnabled === 'number' && (
          <>
            <SimpleTooltip content={t('ringGroups.form.fields.forwardNumber.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
              <Label htmlFor="forwardNumber" className="text-left">
                {t('ringGroups.form.fields.forwardNumber.label')}
              </Label>
            </SimpleTooltip>
            <div className="col-span-12 md:col-span-6 lg:col-span-3">
              <Input
                id="forwardNumber"
                value={formData.forwardNumber}
                onChange={(event) => onFieldChange('forwardNumber', event.target.value)}
                placeholder={t('ringGroups.form.fields.forwardNumber.placeholder')}
              />
            </div>
          </>
        )}

        <SimpleTooltip content={t('ringGroups.form.fields.forwardTollAllow.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="forwardTollAllow" className="text-left">
            {t('ringGroups.form.fields.forwardTollAllow.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="forwardTollAllow"
            value={formData.forwardTollAllow}
            onChange={(event) => onFieldChange('forwardTollAllow', event.target.value)}
            placeholder={t('ringGroups.form.fields.forwardTollAllow.placeholder')}
          />
        </div>

        <SimpleTooltip content={t('ringGroups.form.fields.record.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="record" className="text-left">
            {t('ringGroups.form.fields.record.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3 space-y-2">
          <Toggle
            id="record"
            value={formData.record}
            onChange={(value) => onFieldChange('record', value)}
          />
        </div>

        <SimpleTooltip content={t('ringGroups.form.fields.callTimeoutOverride.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="callTimeoutOverride" className="text-left">
            {t('ringGroups.form.fields.callTimeoutOverride.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="callTimeoutOverride"
            type="number"
            min={1}
            value={formData.callTimeoutOverride}
            onChange={(event) => onFieldChange('callTimeoutOverride', event.target.value)}
            placeholder={t('ringGroups.form.fields.callTimeoutOverride.placeholder')}
          />
        </div>

        <SimpleTooltip content={t('ringGroups.form.fields.presenceId.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="presenceId" className="text-left">
            {t('ringGroups.form.fields.presenceId.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="presenceId"
            value={formData.presenceId}
            onChange={(event) => onFieldChange('presenceId', event.target.value)}
            placeholder={t('ringGroups.form.fields.presenceId.placeholder')}
          />
        </div>
      </div>
    </FormBox>
  );
}

